/* ==========================================================================
   Desktop Sticky Cart — delivery (EDD) grouping module.

   After the drawer content renders, this reads the server-rendered line items
   (data-skuid / data-qty), fetches the cart EDD from the SAME promise-engine
   endpoint the existing cart uses (ONE call for all SKUs), and regroups the item
   nodes into delivery buckets using the response's `shipmentInfo` (warehouse +
   EDD). A single SKU split across warehouses is shown once per shipment with its
   partial quantity + partial price; the soonest shipment row keeps the +/- stepper
   (editing the whole cart line), the rest are read-only.

   Falls back to per-SKU `skuWisEdd` (delivery-time) grouping if shipmentInfo is
   absent. If there's no saved location, grouping is skipped (flat list remains).

   Parallel build: own code, same endpoint. Exposes window.StickyCartEDD.apply().
   ========================================================================== */
(function () {
  'use strict';

  var BASE = 'https://promise-engine-371111.el.r.appspot.com/v2/cartedd';
  var CACHE_KEY = 'stCartShipments';

  function getLocation() {
    try { return JSON.parse(localStorage.getItem('userLocation')) || {}; } catch (e) { return {}; }
  }
  function getCache() {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY)) || {}; } catch (e) { return {}; }
  }
  function setCache(c) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch (e) {}
  }

  function money(paise) {
    var r = Math.round((paise || 0) / 100);
    try { return '₹' + r.toLocaleString('en-IN'); } catch (e) { return '₹' + r; }
  }

  // One cart-level fetch for all SKUs → full response (skuWisEdd + shipmentInfo).
  function fetchCartEdd(skus, qtys, loc) {
    var url =
      BASE +
      '?cpin=' + encodeURIComponent(loc.pincode) +
      '&qty=' + encodeURIComponent(qtys.join(',')) +
      '&skuid=' + encodeURIComponent(skus.join(',')) +
      '&lat=' + encodeURIComponent(loc.latitude || '') +
      '&lng=' + encodeURIComponent(loc.longitude || '');
    return fetch(url).then(function (r) { return r.json(); }).then(function (j) { return j || {}; }).catch(function () { return {}; });
  }

  // Shimmer placeholder shown while the list is being re-grouped (is-edd-pending).
  // 2–3 cards matching the real line-item layout; the real rows are hidden via CSS.
  function showSkeleton(listEl) {
    if (!listEl || listEl.querySelector('[data-st-items-skel]')) return;
    var realCount = listEl.querySelectorAll('.st-cart__item').length;
    var n = Math.max(1, Math.min(3, realCount || 3));
    var card =
      '<div class="st-cart__item-skel">' +
        '<div class="st-cart__item-skel-media st-cart__skel"></div>' +
        '<div class="st-cart__item-skel-info">' +
          '<div class="st-cart__item-skel-line st-cart__skel"></div>' +
          '<div class="st-cart__item-skel-line st-cart__item-skel-line--short st-cart__skel"></div>' +
          '<div class="st-cart__item-skel-price st-cart__skel"></div>' +
        '</div>' +
        '<div class="st-cart__item-skel-qty st-cart__skel"></div>' +
      '</div>';
    var skel = document.createElement('div');
    skel.setAttribute('data-st-items-skel', '');
    var html = '';
    for (var i = 0; i < n; i++) html += card;
    skel.innerHTML = html;
    listEl.appendChild(skel);
  }

  function hideSkeleton(listEl) {
    if (!listEl) return;
    var skel = listEl.querySelector('[data-st-items-skel]');
    if (skel && skel.parentNode) skel.parentNode.removeChild(skel);
  }

  function apply(scope) {
    scope = scope || document;
    var listEl = scope.querySelector('[data-st-items]');
    if (!listEl) return;
    var items = listEl.querySelectorAll('.st-cart__item[data-skuid]');
    if (!items.length) return;

    var loc = getLocation();
    if (!loc || !loc.pincode) return; // no location → leave the flat list as-is

    var bySku = {};
    var order = [];
    Array.prototype.forEach.call(items, function (node) {
      var sku = node.getAttribute('data-skuid');
      if (!sku) return;
      if (!bySku[sku]) { bySku[sku] = { qty: 0, nodes: [] }; order.push(sku); }
      bySku[sku].qty += parseInt(node.getAttribute('data-qty'), 10) || 1;
      bySku[sku].nodes.push(node);
    });
    if (!order.length) return;

    var qtys = order.map(function (s) { return bySku[s].qty; });
    var key = loc.pincode + '|' + order.map(function (s) { return s + ':' + bySku[s].qty; }).sort().join(',');
    var cache = getCache();
    var cached = Object.prototype.hasOwnProperty.call(cache, key);

    // The server renders a flat (ungrouped) list; we regroup it once the cartedd
    // response arrives. A qty change always produces a new cache key, so it always
    // re-fetches — and during that network gap the flat "single card" layout would
    // flash before splitting into delivery groups. Mask the list until grouping
    // resolves. apply() runs synchronously inside bindContent (right after the
    // innerHTML swap), so this class lands before the browser paints. On a cache hit
    // the promise settles on a microtask → added and removed before paint (no flicker).
    listEl.classList.add('is-edd-pending');
    showSkeleton(listEl);

    var p = cached
      ? Promise.resolve(cache[key])
      : fetchCartEdd(order, qtys, loc).then(function (resp) { cache[key] = resp; setCache(cache); return resp; });

    var reveal = function () { hideSkeleton(listEl); listEl.classList.remove('is-edd-pending'); };
    p.then(function (resp) {
      resp = resp || {};
      // Mirror the per-sku cache the review screen reads (skuId → EDD record).
      writeSkuCache(loc, resp.skuWisEdd);
      var shipments = resp.shipmentInfo;
      if (Array.isArray(shipments) && shipments.length) {
        renderByShipment(listEl, bySku, shipments);
      } else {
        renderBySku(listEl, bySku, order, resp.skuWisEdd || []);
      }
    }).then(reveal, reveal);
  }

  // Persist a per-sku EDD record (used by the review screen / analytics) — same shape as before.
  function writeSkuCache(loc, skuWisEdd) {
    if (!Array.isArray(skuWisEdd)) return;
    var c;
    try { c = JSON.parse(sessionStorage.getItem('stCartEddCache')) || {}; } catch (e) { c = {}; }
    skuWisEdd.forEach(function (x) {
      if (!x || !x.skuId) return;
      c[(loc.pincode || '') + '|' + x.skuId] = {
        time: x.deliveryTime, msg: x.webCheckoutMessage || '', type: x.deliveryMessageType || '', dt: x.deliveryDateTime || '',
        minutes: x.deliveryInMinutes, day: x.dayCount,
        // Full analytics edd_response (same 12-field shape used on the other pages),
        // so sticky-cart Segment events can nest edd_response inside each product.
        er: {
          city: x.city || null,
          cluster_id: (x.matchingSLA && x.matchingSLA.cluster && x.matchingSLA.cluster.id != null) ? String(x.matchingSLA.cluster.id) : null,
          cpin: x.cpin || (loc.pincode || null),
          delivery_datetime: x.deliveryDateTime || null,
          delivery_day_count: x.dayCount != null ? String(x.dayCount) : null,
          delivery_eta_minutes: x.deliveryInMinutes != null ? String(x.deliveryInMinutes) : null,
          delivery_speed_type: x.deliveryMessageType || null,
          delivery_timeslot_label: x.deliveryTime || null,
          sku_type: x.skuType || null,
          state: x.state || null,
          warehouse_code: (x.matchingSLA && x.matchingSLA.warehouse && x.matchingSLA.warehouse.name) || null,
          warehouse_id: (x.matchingSLA && x.matchingSLA.warehouse && x.matchingSLA.warehouse.id != null) ? String(x.matchingSLA.warehouse.id) : null
        }
      };
    });
    try { sessionStorage.setItem('stCartEddCache', JSON.stringify(c)); } catch (e) {}
  }

  /* ---- SVGs / header ---------------------------------------------------- */
  var CLOCK_SVG =
    '<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M10.8567 7.01367C10.8567 12.0223 10.8567 7.01367 10.8567 12.0223C5.80469 12.0223 10.8293 12.0496 5.80469 12.0223C5.80469 14.7884 8.06652 17.0309 10.8567 17.0309C13.6468 17.0309 15.9086 14.7884 15.9086 12.0223C15.9086 9.2561 13.6468 7.01367 10.8567 7.01367Z" fill="url(#paint0_linear_81_8350)" />' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M10.852 5.98047C14.1491 5.98047 16.8219 8.67266 16.8219 11.9937C16.8219 15.3147 14.1491 18.0069 10.852 18.0069C7.55498 18.0069 4.88219 15.3147 4.88219 11.9937C4.88219 8.67267 7.55498 5.98047 10.852 5.98047ZM11.6363 4.21994C11.6465 4.75397 11.6395 4.24049 11.6469 4.77829C15.319 5.16511 18.1517 8.24798 18.1517 11.9937C18.1517 16.0012 14.877 19.25 10.8375 19.25C6.79808 19.25 3.52344 16.0012 3.52344 11.9937C3.52344 8.2207 6.41512 5.12025 10.1258 4.77027C10.1159 4.27464 10.1246 4.70981 10.1113 4.21717H9.55836C9.33974 4.21717 9.16088 4.03831 9.16088 3.81969V3.14749C9.16088 2.92887 9.33974 2.75 9.55836 2.75H12.182C12.4006 2.75 12.5794 2.92886 12.5794 3.14749V3.81969C12.5794 4.03831 12.4006 4.21717 12.182 4.21717L11.6363 4.21994Z" fill="url(#paint1_linear_81_8350)" />' +
      '<defs>' +
        '<linearGradient id="paint0_linear_81_8350" x1="10.8567" y1="7.01367" x2="10.8567" y2="17.0309" gradientUnits="userSpaceOnUse">' +
          '<stop stop-color="#FFBF8B" />' +
          '<stop offset="1" stop-color="#FF7A0C" />' +
        '</linearGradient>' +
        '<linearGradient id="paint1_linear_81_8350" x1="10.8375" y1="2.75" x2="10.8375" y2="19.25" gradientUnits="userSpaceOnUse">' +
          '<stop stop-color="#FFBF8B" />' +
          '<stop offset="1" stop-color="#FF7A0C" />' +
        '</linearGradient>' +
      '</defs>' +
    '</svg>'

  var NOW_SVG = 
    '<svg width="47" height="15" viewBox="0 0 47 15" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d = "M8.22864 3.11328C9.05825 3.11329 9.74972 3.28104 10.3029 3.61719C10.8714 3.95348 11.2716 4.47361 11.5021 5.17676C11.7479 5.8646 11.7863 6.7517 11.6173 7.83691L10.5568 14.5547H7.67493L8.73547 7.88184C8.8276 7.31645 8.82765 6.85017 8.73547 6.4834C8.64327 6.11676 8.45917 5.84159 8.18274 5.6582C7.92155 5.45952 7.55255 5.36038 7.07629 5.36035C6.47699 5.36035 5.94648 5.49014 5.48547 5.75C5.03991 6.00985 4.67904 6.37679 4.40247 6.85059C4.14123 7.30916 3.95632 7.86712 3.84875 8.52441L2.88098 14.5547H0.00012207L1.79797 3.34277H4.6095L4.31848 5.13379C4.67599 4.55464 5.13332 4.09405 5.69348 3.75488C6.41563 3.32704 7.26072 3.11328 8.22864 3.11328Z" fill = "black" />' +
      '<path d="M23.7837 6.4344C23.92 6.33811 24.7798 5.74649 24.8962 5.66301C25.0673 5.90374 25.2233 6.12124 25.2482 6.14601C25.2895 6.19222 25.3435 6.22539 25.4034 6.24145C25.4633 6.25751 25.5266 6.25577 25.5855 6.23645C25.6803 6.19855 25.7704 6.15006 25.8543 6.0919C25.9388 6.03486 26.0166 5.96861 26.0864 5.89437C26.1259 5.84654 26.1506 5.78825 26.1575 5.72662C26.1644 5.66498 26.1532 5.60268 26.1252 5.54731C26.0933 5.48045 25.4263 4.47061 25.3479 4.36163C25.2757 4.24911 24.577 3.26017 24.5264 3.20632C24.485 3.16011 24.4311 3.12694 24.3712 3.11088C24.3112 3.09482 24.248 3.09656 24.189 3.11589C23.9997 3.19315 23.829 3.30975 23.6881 3.45796C23.6486 3.50579 23.6239 3.56408 23.617 3.62571C23.6101 3.68735 23.6213 3.74965 23.6493 3.80502C23.664 3.83525 23.8096 4.06056 23.9714 4.30868C23.8645 4.37714 23.0168 4.9536 22.8935 5.03432L23.7837 6.4344ZM20.6741 3.11802C20.6989 2.96913 20.8377 2.21713 20.8593 2.08536C21.2702 2.15511 21.6441 2.21583 21.6908 2.21724C21.8612 2.22986 22.0498 2.1269 22.1187 1.96949C22.1753 1.83711 22.2169 1.69886 22.2428 1.55728C22.2703 1.416 22.2819 1.27207 22.2773 1.1282C22.2714 0.955805 22.1331 0.791832 21.9706 0.74202C21.872 0.707204 20.1978 0.364275 20.0115 0.334479C19.8271 0.294416 18.1429 0.00453083 18.0384 0.00108537C17.8681 -0.0118142 17.6795 0.0914309 17.6105 0.248835C17.5539 0.381161 17.5123 0.519431 17.4865 0.661045C17.459 0.802331 17.4474 0.946261 17.452 1.09013C17.4589 1.26183 17.5971 1.42637 17.7604 1.47663C17.8053 1.49246 18.1757 1.572 18.5829 1.65642C18.5551 1.78702 18.4103 2.53789 18.3795 2.68565L20.6741 3.11802Z" fill="#292828"/>' +
      '<path d="M19.5543 2.761C16.2042 2.12974 12.977 4.33358 12.3458 7.68364C11.7145 11.0337 13.9192 14.2611 17.2684 14.8922C20.6176 15.5233 23.8457 13.3196 24.4769 9.96953C25.1082 6.61947 22.9049 3.39236 19.5543 2.761Z" fill="#292828"/>' +
        '<path d="M23.6651 5.58596C23.215 4.85432 22.6166 4.22501 21.9086 3.73848C21.2007 3.25196 20.3987 2.91899 19.5543 2.761C16.2042 2.12974 12.977 4.33358 12.3458 7.68364C12.1298 8.82138 12.2399 9.99698 12.6633 11.0749C13.2118 11.3121 13.7851 11.4876 14.3726 11.5981C18.5981 12.3943 22.6737 9.72219 23.6651 5.58596Z" fill="black"/>' +
        '<path d="M15.9203 13.9359L15.34 13.7056L17.5144 9.96137L15.4833 9.1553C15.1467 9.02172 15.2254 8.84233 15.4131 8.69304C15.6008 8.54375 15.4603 8.65913 15.481 8.64102C16.7488 7.64355 18.6489 6.15985 21.18 4.17626L21.7603 4.40656L19.5859 8.15081L21.617 8.95688C21.9013 9.06973 21.8668 9.27323 21.7736 9.3547L21.6988 9.42375C17.8433 12.4284 15.9203 13.9359 15.9203 13.9359Z" fill="#C0F42D"/>' +
      '<path d="M31.0579 10.9297L35.2083 3.11328H37.2366L38.8801 11.082L43.0686 3.11328H46.0188L39.7952 14.3252H37.2825L35.6838 7.09863L31.8196 14.3252H29.3303L26.6799 3.11328H29.4456L31.0579 10.9297Z" fill="black"/>' +
    '</svg >'

  var INFO_SVG =
    '<svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M5.53045 1C2.7637 1 0.5 3.2637 0.5 6.03045C0.5 8.79719 2.7637 11.0609 5.53045 11.0609C8.29719 11.0609 10.5609 8.79719 10.5609 6.03045C10.5609 3.2637 8.29719 1 5.53045 1ZM5.53045 10.0548C3.31705 10.0548 1.50609 8.24384 1.50609 6.03045C1.50609 3.81705 3.31705 2.00609 5.53045 2.00609C7.74384 2.00609 9.5548 3.81705 9.5548 6.03045C9.5548 8.24384 7.74384 10.0548 5.53045 10.0548Z" fill="#6D6D6D" />' +
      '<path d="M5.52763 4.86572C5.21574 4.86572 5.00781 5.07253 5.00781 5.38274V7.96783C5.00781 8.27804 5.21574 8.48485 5.52763 8.48485C5.83951 8.48485 6.04744 8.27804 6.04744 7.96783V5.38274C6.04744 5.07253 5.83951 4.86572 5.52763 4.86572Z" fill="#6D6D6D" />' +
      '<path d="M5.52763 4.47544C5.81471 4.47544 6.04744 4.24397 6.04744 3.95842C6.04744 3.67288 5.81471 3.44141 5.52763 3.44141C5.24054 3.44141 5.00781 3.67288 5.00781 3.95842C5.00781 4.24397 5.24054 4.47544 5.52763 4.47544Z" fill="#6D6D6D" />' +
    '</svg>'

  var INFO_SVG2 =
    '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<g clip-path="url(#clip0_109_6340)">' +
        '<path d="M6 0C2.6865 0 0 2.6865 0 6C0 9.3135 2.6865 12 6 12C9.3135 12 12 9.3135 12 6C12 2.6865 9.3135 0 6 0ZM5.25 5.25C5.25 5.05109 5.32902 4.86032 5.46967 4.71967C5.61032 4.57902 5.80109 4.5 6 4.5C6.19891 4.5 6.38968 4.57902 6.53033 4.71967C6.67098 4.86032 6.75 5.05109 6.75 5.25V9C6.75 9.19891 6.67098 9.38968 6.53033 9.53033C6.38968 9.67098 6.19891 9.75 6 9.75C5.80109 9.75 5.61032 9.67098 5.46967 9.53033C5.32902 9.38968 5.25 9.19891 5.25 9V5.25ZM6 3.762C5.90148 3.76198 5.80394 3.74255 5.71293 3.70482C5.62192 3.6671 5.53924 3.61182 5.46959 3.54214C5.39995 3.47246 5.34471 3.38975 5.30703 3.29872C5.26935 3.2077 5.24998 3.11014 5.25 3.01163C5.25002 2.91311 5.26945 2.81556 5.30718 2.72456C5.3449 2.63355 5.40018 2.55086 5.46986 2.48122C5.53954 2.41157 5.62225 2.35634 5.71328 2.31866C5.8043 2.28098 5.90186 2.2616 6.00037 2.26163C6.19934 2.26167 6.39013 2.34076 6.53078 2.48148C6.67143 2.62221 6.75042 2.81304 6.75037 3.012C6.75033 3.21096 6.67124 3.40176 6.53052 3.54241C6.38979 3.68306 6.19896 3.76205 6 3.762Z" fill="url(#paint0_linear_109_6340)" />' +
      '</g>' +
      '<defs>' +
        '<linearGradient id="paint0_linear_109_6340" x1="6" y1="12" x2="6" y2="-0.428572" gradientUnits="userSpaceOnUse">' +
      '<stop stop-color="#363434" />' +
      '<stop offset="1" stop-color="#929292" />' +
      '</linearGradient>' +
      '<clipPath id="clip0_109_6340">' +
      '<rect width="12" height="12" fill="white" />' +
      '</clipPath>' +
      '</defs>' +
    '</svg>';

  var DOT_SVG = 
    '<svg width="4" height="4" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<circle cx="2" cy="2" r="2" fill="#C0C0C0" />' +
    '</svg>'

  function buildGroupHead(time, type, idx, total, count, hasRx) {
    var head = document.createElement('div');
    head.className = 'st-cart__group-head';
    var chipHtml = '';
    if (time) {
      var isNow = String(type || '').toLowerCase() === 'now';
      chipHtml =
        '<span class="st-cart__eta-chip' + (isNow ? ' is-now' : '') + '">' +
      CLOCK_SVG + (isNow ? NOW_SVG : '') + '<span class="st-cart__eta-time">' + escapeHtml(time) + '</span></span>';
    }
    head.innerHTML =
      chipHtml +
      '<span class="st-cart__group-meta">' +
      count + ' item' + (count > 1 ? 's' : '') +
      DOT_SVG + ' Delivery ' + (idx + 1) + '</span>';
    return head;
  }

  function rxNote() {
    var note = document.createElement('div');
    note.className = 'st-cart__group-verify';
    // The ⓘ here opens the "Why delivery time may change" modal.
    note.innerHTML =
      '<span>Delivery time depends on verification process</span>' +
      '<span class="st-cart__group-verify-info" data-st-dly-open>' + INFO_SVG2 + '</span>';
    return note;
  }

  // Recompute a (split) row's price from its per-unit data attrs × the partial qty.
  function setPartialPrice(row, qty) {
    var unit = parseInt(row.getAttribute('data-unit-price'), 10);
    if (!isNaN(unit)) {
      var sell = row.querySelector('.st-cart__item-selling');
      if (sell) sell.textContent = money(unit * qty);
    }
    var mrp = parseInt(row.getAttribute('data-unit-mrp'), 10);
    var mrpEl = row.querySelector('.st-cart__item-mrp');
    if (mrpEl && !isNaN(mrp) && mrp > 0) mrpEl.textContent = 'MRP ' + money(mrp * qty);
  }

  /* ---- Group by shipmentInfo (warehouse + EDD), splitting partial lines --- */
  function renderByShipment(listEl, bySku, shipments) {
    var sorted = shipments.slice().sort(function (a, b) {
      return new Date(a.deliveryDateTime || 0) - new Date(b.deliveryDateTime || 0);
    });

    // How many shipments contain each SKU → >1 means the line is split.
    var shipCount = {};
    sorted.forEach(function (sh) {
      (sh.items || []).forEach(function (it) { shipCount[it.skuId] = (shipCount[it.skuId] || 0) + 1; });
    });
    var usedFirst = {}; // sku → original (stepper) node already placed?

    var total = sorted.length;
    var frag = document.createDocumentFragment();
    var anyRendered = false;

    sorted.forEach(function (sh, idx) {
      var rows = [];
      var hasRx = false;
      (sh.items || []).forEach(function (it) {
        var bucket = bySku[it.skuId];
        if (!bucket || !bucket.nodes.length) return;

        if (shipCount[it.skuId] <= 1) {
          // Whole line in a single shipment — move the original node(s) in unchanged.
          bucket.nodes.forEach(function (n) { rows.push(n); if (n.getAttribute('data-rx') === 'true') hasRx = true; });
          return;
        }
        // Split: one row per shipment with the partial qty. EVERY row keeps a stepper;
        // each edits the WHOLE cart line (data-line-qty) since it's one Shopify line —
        // +/- changes the line total, then the cart re-renders and re-splits.
        var first = !usedFirst[it.skuId];
        usedFirst[it.skuId] = true;
        var template = bucket.nodes[0];
        var row = first ? template : template.cloneNode(true);
        var num = row.querySelector('[data-st-qty-num]');
        if (num) num.textContent = it.qty;
        setPartialPrice(row, it.qty);
        Array.prototype.forEach.call(row.querySelectorAll('[data-st-qty-inc],[data-st-qty-dec]'), function (b) {
          b.setAttribute('data-line-qty', bucket.qty);
        });
        if (!first) row.setAttribute('data-st-split', '1');
        if (row.getAttribute('data-rx') === 'true') hasRx = true;
        rows.push(row);
      });

      if (!rows.length) return;
      anyRendered = true;
      var groupLi = document.createElement('li');
      groupLi.className = 'st-cart__group';
      groupLi.appendChild(buildGroupHead(sh.deliveryTime, sh.deliveryMessageType, idx, total, rows.length, hasRx));
      if (hasRx) groupLi.appendChild(rxNote());
      var inner = document.createElement('ul');
      inner.className = 'st-cart__group-items';
      rows.forEach(function (n) { inner.appendChild(n); });
      groupLi.appendChild(inner);
      frag.appendChild(groupLi);
      // Analytics: Delivery Group Viewed (once per group per open).
      var A = window.StickyCartAnalytics;
      if (A && typeof A.deliveryGroupViewed === 'function') {
        A.deliveryGroupViewed(sh.deliveryMessageType || sh.deliveryTime || ('group_' + idx), sh.deliveryTime || '', rows.length, idx + 1, String(sh.deliveryMessageType || '').toLowerCase() === 'now');
      }
    });

    if (!anyRendered) return;
    listEl.classList.add('is-grouped');
    listEl.innerHTML = '';
    listEl.appendChild(frag);
  }

  /* ---- Fallback: per-SKU grouping by delivery time --------------------- */
  function renderBySku(listEl, bySku, order, skuWisEdd) {
    var recBySku = {};
    (skuWisEdd || []).forEach(function (x) {
      if (x && x.skuId && !recBySku[x.skuId]) {
        recBySku[x.skuId] = { time: x.deliveryTime, type: x.deliveryMessageType || '', minutes: x.deliveryInMinutes, day: x.dayCount };
      }
    });

    var groups = {};
    var groupOrder = [];
    order.forEach(function (sku) {
      var rec = recBySku[sku];
      var key = rec && rec.time ? rec.time : '__none__';
      if (!groups[key]) { groups[key] = { rec: rec, nodes: [] }; groupOrder.push(key); }
      bySku[sku].nodes.forEach(function (n) { groups[key].nodes.push(n); });
    });
    if (groupOrder.length === 1 && groupOrder[0] === '__none__') return;

    groupOrder.sort(function (a, b) {
      if (a === '__none__') return 1;
      if (b === '__none__') return -1;
      var da = groups[a].rec, db = groups[b].rec;
      var ka = da && da.day != null ? da.day : 999, kb = db && db.day != null ? db.day : 999;
      if (ka !== kb) return ka - kb;
      var ma = da && da.minutes != null ? da.minutes : 1e9, mb = db && db.minutes != null ? db.minutes : 1e9;
      return ma - mb;
    });

    var total = groupOrder.length;
    var frag = document.createDocumentFragment();
    groupOrder.forEach(function (key, idx) {
      var g = groups[key];
      var hasRx = g.nodes.some(function (n) { return n.getAttribute('data-rx') === 'true'; });
      var groupLi = document.createElement('li');
      groupLi.className = 'st-cart__group';
      groupLi.appendChild(buildGroupHead(g.rec && g.rec.time, g.rec && g.rec.type, idx, total, g.nodes.length, hasRx));
      if (hasRx) groupLi.appendChild(rxNote());
      var inner = document.createElement('ul');
      inner.className = 'st-cart__group-items';
      g.nodes.forEach(function (n) { inner.appendChild(n); });
      groupLi.appendChild(inner);
      frag.appendChild(groupLi);
      var A = window.StickyCartAnalytics;
      if (A && typeof A.deliveryGroupViewed === 'function') {
        A.deliveryGroupViewed((g.rec && g.rec.type) || (g.rec && g.rec.time) || ('group_' + idx), (g.rec && g.rec.time) || '', g.nodes.length, idx + 1, String((g.rec && g.rec.type) || '').toLowerCase() === 'now');
      }
    });

    listEl.classList.add('is-grouped');
    listEl.innerHTML = '';
    listEl.appendChild(frag);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ---- "Why delivery time may change" modal ------------------------------
     Opened by the delivery-group info icon (the ⓘ next to "Delivery N"). A centered
     popup over a scrim with static informational copy (Figma 425-16553). Built once,
     lazily, and reused. */
  var MAP_IMG = 'https://cdn.shopify.com/s/files/1/0565/8021/0861/files/map_img.png?v=1781766428';
  var dlyModal;

  function buildDlyModal() {
    if (dlyModal) return dlyModal;
    var wrap = document.createElement('div');
    wrap.className = 'st-dly';
    wrap.hidden = true;
    wrap.innerHTML =
      '<div class="st-dly__scrim" data-st-dly-close></div>' +
      '<div class="st-dly__dialog" role="dialog" aria-modal="true" aria-label="Why delivery time may change">' +
        '<button type="button" class="st-dly__close" data-st-dly-close aria-label="Close">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
            '<path d="M6 6l12 12M18 6L6 18" stroke="#1a1a1a" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="st-dly__card">' +
          '<div class="st-dly__head">' +
            '<h3 class="st-dly__title">Why delivery time may change</h3>' +
            '<img class="st-dly__map" src="' + MAP_IMG + '" alt="" width="105">' +
          '</div>' +
          '<div class="st-dly__panel"><ul class="st-dly__list">' +
            '<li>After verification, we confirm the final delivery time and proceed with dispatch.</li>' +
            '<li>Certain medicines need a valid prescription to ensure safe and responsible delivery.</li>' +
            '<li>Our pharmacist reviews your prescription to verify authenticity and dosage.</li>' +
          '</ul></div>' +
          '<button type="button" class="st-dly__ok" data-st-dly-close>Okay, Got It</button>' +
        '</div>' +
      '</div>';
    wrap.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-st-dly-close]')) closeDlyModal();
    });
    document.body.appendChild(wrap);
    dlyModal = wrap;
    return wrap;
  }

  function openDlyModal() {
    var m = buildDlyModal();
    m.hidden = false;
    void m.offsetWidth; // reflow so the open transition runs
    m.classList.add('is-open');
    document.addEventListener('keydown', dlyKey);
  }
  function closeDlyModal() {
    if (!dlyModal) return;
    dlyModal.classList.remove('is-open');
    document.removeEventListener('keydown', dlyKey);
    setTimeout(function () { if (dlyModal && !dlyModal.classList.contains('is-open')) dlyModal.hidden = true; }, 220);
  }
  function dlyKey(e) { if (e.key === 'Escape') closeDlyModal(); }

  // The verify-note ⓘ is rendered dynamically, so delegate the click.
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('[data-st-dly-open]')) openDlyModal();
  });

  window.StickyCartEDD = { apply: apply };
})();
